/**
 * Predictive search dropdown for the Vault navbar.
 *
 * Horizon ships a predictive search of its own, but it is bound to a Cmd+K
 * dialog (see predictive-search.js). This drives the inline nav field instead,
 * talking to Shopify's /search/suggest.json directly so it stays independent of
 * that component's dialog wiring.
 */
(function () {
  var LIMIT = 8;
  var DEBOUNCE_MS = 200;

  function money(value, iso) {
    var n = parseFloat(value);
    if (isNaN(n)) return '';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: iso }).format(n);
    } catch (e) {
      return '$' + n.toFixed(2);
    }
  }

  /**
   * suggest.json returns featured_image as an object ({url, alt, ...}) but image
   * as a bare string, so normalise before either reaches thumb().
   */
  function imageOf(product) {
    var fi = product.featured_image;
    if (fi && typeof fi === 'object') return fi.url || '';
    if (typeof fi === 'string') return fi;
    return typeof product.image === 'string' ? product.image : '';
  }

  function altOf(product) {
    var fi = product.featured_image;
    return fi && typeof fi === 'object' && fi.alt ? fi.alt : '';
  }

  /** Shopify CDN URLs accept a width param; ask for a thumbnail, not the full asset. */
  function thumb(url) {
    if (typeof url !== 'string' || !url) return '';
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=96';
  }

  /** Wrap occurrences of the typed term so the match reads like the suggestion list it mimics. */
  function highlight(text, term) {
    var frag = document.createDocumentFragment();
    var haystack = text.toLowerCase();
    var needle = term.toLowerCase();
    var from = 0;
    var at;

    if (!needle) {
      frag.appendChild(document.createTextNode(text));
      return frag;
    }

    while ((at = haystack.indexOf(needle, from)) !== -1) {
      if (at > from) frag.appendChild(document.createTextNode(text.slice(from, at)));
      var mark = document.createElement('strong');
      mark.textContent = text.slice(at, at + needle.length);
      frag.appendChild(mark);
      from = at + needle.length;
    }
    if (from < text.length) frag.appendChild(document.createTextNode(text.slice(from)));
    return frag;
  }

  function init(form) {
    var input = form.querySelector('.vault-nav__search-input');
    if (!input) return;

    var iso = form.getAttribute('data-currency') || 'USD';
    var panel = document.createElement('div');
    panel.className = 'vault-nav__search-panel';
    panel.hidden = true;
    form.appendChild(panel);

    var timer = null;
    var controller = null;
    var items = [];
    var active = -1;

    function close() {
      panel.hidden = true;
      panel.innerHTML = '';
      items = [];
      active = -1;
    }

    function setActive(next) {
      if (active >= 0 && items[active]) items[active].classList.remove('is-active');
      active = next;
      if (active >= 0 && items[active]) {
        items[active].classList.add('is-active');
        items[active].scrollIntoView({ block: 'nearest' });
      }
    }

    function render(products, term) {
      panel.innerHTML = '';
      items = [];
      active = -1;

      if (!products.length) {
        var empty = document.createElement('p');
        empty.className = 'vault-nav__search-empty';
        empty.textContent = 'No matches for "' + term + '"';
        panel.appendChild(empty);
        panel.hidden = false;
        return;
      }

      products.forEach(function (p) {
        var row = document.createElement('a');
        row.className = 'vault-nav__search-item';
        row.href = p.url;

        var src = thumb(imageOf(p));
        if (src) {
          var img = document.createElement('img');
          img.className = 'vault-nav__search-thumb';
          img.src = src;
          img.alt = altOf(p);
          img.loading = 'lazy';
          img.width = 40;
          img.height = 40;
          row.appendChild(img);
        } else {
          // Keep the row aligned with its neighbours when a product has no image.
          var blank = document.createElement('span');
          blank.className = 'vault-nav__search-thumb';
          row.appendChild(blank);
        }

        var text = document.createElement('span');
        text.className = 'vault-nav__search-text';

        var name = document.createElement('span');
        name.className = 'vault-nav__search-name';
        name.appendChild(highlight(p.title, term));
        text.appendChild(name);

        var meta = document.createElement('span');
        meta.className = 'vault-nav__search-meta';
        meta.textContent = p.available === false ? 'Sold out' : money(p.price, iso);
        text.appendChild(meta);

        row.appendChild(text);
        panel.appendChild(row);
        items.push(row);
      });

      var all = document.createElement('button');
      all.type = 'submit';
      all.className = 'vault-nav__search-all';
      all.textContent = 'See all results for "' + term + '"';
      panel.appendChild(all);

      panel.hidden = false;
    }

    function query(term) {
      if (controller) controller.abort();
      controller = new AbortController();

      var url =
        '/search/suggest.json?q=' + encodeURIComponent(term) +
        '&resources[type]=product' +
        '&resources[limit]=' + LIMIT +
        '&resources[options][unavailable_products]=hide' +
        '&resources[options][fields]=title,vendor,product_type,variants.title';

      fetch(url, { signal: controller.signal })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
        .then(function (data) {
          var products = (data.resources && data.resources.results && data.resources.results.products) || [];
          render(products, term);
        })
        .catch(function (err) {
          // An aborted request is the expected outcome of typing another key.
          if (err && err.name === 'AbortError') return;
          console.error('[vault-nav-search]', err);
          close();
        });
    }

    input.addEventListener('input', function () {
      var term = input.value.trim();
      clearTimeout(timer);
      if (term.length < 2) {
        close();
        return;
      }
      timer = setTimeout(function () { query(term); }, DEBOUNCE_MS);
    });

    input.addEventListener('keydown', function (e) {
      if (panel.hidden || !items.length) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive(active < items.length - 1 ? active + 1 : 0);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive(active > 0 ? active - 1 : items.length - 1);
      } else if (e.key === 'Enter') {
        if (active >= 0 && items[active]) {
          e.preventDefault();
          window.location.href = items[active].href;
        }
      } else if (e.key === 'Escape') {
        close();
      }
    });

    input.addEventListener('focus', function () {
      if (input.value.trim().length >= 2 && !items.length) query(input.value.trim());
    });

    document.addEventListener('click', function (e) {
      if (!form.contains(e.target)) close();
    });
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('form.vault-nav__search'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
